import { useEffect, useState, type ReactNode } from 'react'
import { Button } from './Button.tsx'

/**
 * Running a golden-set case, and what came back.
 *
 * One shell for both sets, because it is one act asked of two answer keys:
 * press the same button, wait for the same kind of model call, read the same
 * shape of answer -- the expected side, the side we produced, and where they
 * part. The two sets had grown different words for it and different places to
 * show it, which made them look like different features.
 *
 * **The waiting is part of it.** A run is a model call and takes seconds, so
 * the modal opens first and says so, with the clock running. A button that
 * greys out for half a minute and then repaints a page is the same wait with
 * nothing to look at, and invites a second press that costs another call.
 *
 * What differs between the sets is the table, which is the caller's -- a
 * reading compares fields, a resolution compares outcomes and carries the
 * reasoning that produced them.
 */
export function RunModal({
  title,
  caseId,
  busy,
  error,
  metrics,
  notes,
  children,
  onClose,
}: {
  title: string
  caseId: string
  busy: boolean
  error?: string | undefined
  /** The score, as a strip. Absent while it is still running. */
  metrics?: ReactNode
  /** What changes the meaning of the number: a draft label, a missing detector. */
  notes?: ReactNode
  children?: ReactNode
  onClose: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${title}: ${caseId}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
    >
      <div
        className="rounded-base bg-background-raised border-stroke-weak flex max-h-[85vh] w-full max-w-[72rem] flex-col gap-3 overflow-y-auto border p-4"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-tPSemi text-text-strong">{title}</h2>
            <span className="text-tSmall text-text-weak">{caseId}</span>
          </div>
          {/*
            Closing mid-run is allowed, and does not stop the run: it is a
            request already in flight, and pretending otherwise would be a
            cancel button that cancels nothing.
          */}
          <Button variant="tertiary" tone="neutral" size="small" onClick={onClose}>
            {busy ? 'Fechar e deixar rodando' : 'Fechar'}
          </Button>
        </div>

        {busy && <Waiting />}

        {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

        {metrics}
        {notes}
        {children}
      </div>
    </div>
  )
}

/**
 * The wait, with the clock running.
 *
 * Elapsed rather than a percentage: nothing here knows how far along a model
 * call is, and a progress bar that invents one is a lie that makes the next
 * wait feel longer. The steps are named because they are the honest ones --
 * this is the order every run goes in.
 */
function Waiting() {
  const [seconds, setSeconds] = useState(0)

  useEffect(() => {
    const tick = setInterval(() => setSeconds((n) => n + 1), 1000)
    return () => clearInterval(tick)
  }, [])

  return (
    <div className="rounded-base border-stroke-weak bg-fill-weak/40 flex flex-col gap-1 border p-3">
      <p className="text-tP text-text-strong" role="status" aria-live="polite">
        Rodando… {seconds}s
      </p>
      <p className="text-tSmall text-text-weak">
        Páginas, blocos, leitura e pontuação. Leva alguns segundos porque é uma chamada de modelo de
        verdade — a mesma que a conferência faz.
      </p>
    </div>
  )
}

/** One figure in the strip above a run's table. */
export function RunMetric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string
  tone?: 'neutral' | 'ok' | 'warn' | 'error'
}) {
  const TONE: Readonly<Record<string, string>> = {
    neutral: 'text-text-strong',
    ok: 'text-text-success',
    warn: 'text-text-warning',
    error: 'text-text-error',
  }
  return (
    <div className="flex flex-col">
      <span className="text-tTiny text-text-weak font-semibold uppercase">{label}</span>
      <span className={`text-tPSemi tabular-nums ${TONE[tone] ?? TONE['neutral']}`}>{value}</span>
    </div>
  )
}
